import { Flex, Icon, Text, useBoolean } from '@chakra-ui/react'
import { useMemo } from 'react'
import React from 'react'
import IconBlocked from '~/assets/svgs/icon-blocked.svg?react'
import IconCallStart from '~/assets/svgs/icon-call-start.svg?react'
import IconContacts from '~/assets/svgs/icon-contacts.svg?react'
import IconFax from '~/assets/svgs/icon-fax.svg?react'
import IconMessaging from '~/assets/svgs/icon-messaging.svg?react'
import {
  useBlockedNumbers,
  useCanBlockNumber,
  useFindBlockedNumber,
} from '~/atoms/BlockedNumbersState'
import { useModalState } from '~/atoms/ModalState'
import { ExtensionBadgeVariant } from '~/components/shared/componentStyles/ExtensionBadge.style'
import { QuickActionButtonVariants } from '~/components/shared/componentStyles/QuickActionButton.style'
import { ExtensionBadge } from '~/components/shared/ExtensionBadge'
import { QuickActionButton } from '~/components/shared/QuickActionButton'
import { CallTarget } from '~/pages/Calls/data/CallDetailData'
import { ContactDetails } from '~/pages/Contacts/viewModels/Contacts.viewmodel'
import { textStyleKeys } from '~/themes/textStyles'
import { Size } from '~/types/ThemeTypes'

import { toNounCasing } from '@mangovoice/core'

interface ContactInfoCardProps {
  callTarget: CallTarget
  title: string
  subtitle: string
  contact?: ContactDetails
  number: string
  isExtension?: boolean
  location?: string
  locationIsTitle?: boolean
}
export const ContactInfoCard = ({
  callTarget,
  title,
  subtitle,
  contact,
  number,
  isExtension,
  location,
  locationIsTitle,
}: ContactInfoCardProps) => {
  const [isHovered, setIsHovered] = useBoolean(false)

  const { blockedNumbers, unblockNumber, blockNumberWithConfirmation } =
    useBlockedNumbers()
  const canBlockNumber = useCanBlockNumber(number)
  const blockedNumber = useFindBlockedNumber(blockedNumbers, number)

  const {
    callModal: [, setCallModalState],
    faxModal: [faxModalState, setFaxModalState],
    messagingModal: [, setMessagingModalState],
  } = useModalState()

  const handleCallNumber = () => {
    const callee =
      contact !== undefined && contact.defaultNumber !== undefined
        ? { contactNumber: contact.defaultNumber, contact: contact }
        : number
    setCallModalState({
      open: true,
      callee: callee,
    })
  }
  const handleMessageNumber = () => {
    setMessagingModalState({ open: true, number })
  }
  const handleSendFax = () => {
    setFaxModalState({ ...faxModalState, open: true, number })
  }

  const blockNumberButton = useMemo(() => {
    if (blockedNumber) {
      return (
        <QuickActionButton
          variant={QuickActionButtonVariants.Default_100}
          size={Size.sm}
          actionIcon={IconBlocked}
          tooltipText="Unblock Number"
          onClick={() =>
            unblockNumber(blockedNumber.id, blockedNumber.phoneNumber)
          }
        />
      )
    } else if (canBlockNumber) {
      return (
        <QuickActionButton
          variant={QuickActionButtonVariants.Default_100}
          size={Size.sm}
          actionIcon={IconBlocked}
          tooltipText="Block Number"
          onClick={() => blockNumberWithConfirmation(number)}
        />
      )
    }
    return null
  }, [blockedNumber, canBlockNumber, number])

  return (
    <Flex
      data-testid="contact-info-card"
      flexDirection="column"
      alignSelf="stretch"
      alignItems="flex-start"
      gap="0.25rem"
      flex="1 0 0"
      onMouseEnter={setIsHovered.on}
      onMouseLeave={setIsHovered.off}
    >
      <Flex
        data-testid="contact-info-card-header"
        height="1.75rem"
        justifyContent="space-between"
        alignItems="center"
        alignSelf="stretch"
      >
        <Text textStyle={textStyleKeys.Body1_Bold}>
          {toNounCasing(callTarget)}
        </Text>
        {isHovered && (
          <Flex gap="0.5rem">
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_100}
              size={Size.sm}
              actionIcon={IconCallStart}
              tooltipText="Call"
              onClick={handleCallNumber}
            />
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_100}
              size={Size.sm}
              actionIcon={IconMessaging}
              tooltipText="Message"
              onClick={handleMessageNumber}
            />
            <QuickActionButton
              variant={QuickActionButtonVariants.Default_100}
              size={Size.sm}
              actionIcon={IconFax}
              tooltipText="Send Fax"
              onClick={handleSendFax}
            />
            {blockNumberButton}
          </Flex>
        )}
      </Flex>
      <Flex
        data-testid="contact-info-card-content"
        flexDirection="column"
        alignItems="flex-start"
        gap="0.25rem"
        alignSelf="stretch"
      >
        <Flex
          data-testid="contact-info-card-row"
          alignItems="center"
          gap="0.25rem"
          alignSelf="stretch"
        >
          {contact?.id && <Icon as={IconContacts} />}
          <Text textStyle={textStyleKeys.Body1} noOfLines={1}>
            {title}
          </Text>
        </Flex>
        <Flex
          data-testid="contact-info-card-row"
          alignItems="center"
          gap="0.25rem"
          alignSelf="stretch"
        >
          {isExtension && (
            <ExtensionBadge
              extension={number}
              variant={ExtensionBadgeVariant.LightGray}
              size={Size.sm}
            />
          )}
          <Text textStyle={textStyleKeys.Body1} noOfLines={1}>
            {subtitle}
          </Text>
        </Flex>
        {!locationIsTitle && (
          <Flex
            data-testid="contact-info-card-row"
            alignItems="center"
            gap="0.25rem"
            alignSelf="stretch"
          >
            <Text textStyle={textStyleKeys.Body1} noOfLines={1}>
              {location}
            </Text>
          </Flex>
        )}
      </Flex>
    </Flex>
  )
}
